import { useEffect, useRef } from 'react';
import { TAppEnvironment } from '../../environment/types-app-environment';
import serviceLogger from '../logging/service-logger';
import { consoleLogger } from '../logging/service-console-logger';

declare global {
  interface Window {
    invokeSurveybyTriggerName?: (surveyName: string, triggerName: string) => void;
  }
}

export const OCV_NSAT_TRIGGER_DELAY_MS = 5_000;

type TOcvNsatSurveyConfig = TAppEnvironment['ocvNsatSurvey'];

const isValidConfiguration = (configuration?: TOcvNsatSurveyConfig): configuration is { surveyName: string; triggerName: string } =>
  Boolean(configuration?.surveyName?.trim() && configuration.triggerName?.trim());

const getOcvHost = (): Window => {
  try {
    const host = window.top ?? window;
    void host.invokeSurveybyTriggerName;
    return host;
  } catch {
    return window;
  }
};

export const useOcvSurveyTrigger = (enabled: boolean, configuration?: TOcvNsatSurveyConfig): void => {
  const hasAttemptedRef = useRef(false);
  const configurationErrorLoggedRef = useRef(false);

  useEffect(() => {
    if (!enabled || hasAttemptedRef.current) {
      return;
    }

    if (!isValidConfiguration(configuration)) {
      if (!configurationErrorLoggedRef.current) {
        configurationErrorLoggedRef.current = true;
        serviceLogger.error({
          error: new Error('OCV NSAT survey trigger is enabled without valid survey configuration'),
        });
      }
      return;
    }

    configurationErrorLoggedRef.current = false;

    const timeoutId = window.setTimeout(() => {
      hasAttemptedRef.current = true;

      try {
        const host = getOcvHost();
        const invokeSurvey = host.invokeSurveybyTriggerName;

        if (typeof invokeSurvey !== 'function') {
          consoleLogger.warn('OCV Integration is unavailable');
          return;
        }

        consoleLogger.log('Invoking OCV NSAT survey trigger', {
          surveyName: configuration.surveyName,
          triggerName: configuration.triggerName,
        });
        invokeSurvey.call(host, configuration.surveyName, configuration.triggerName);
      } catch (error) {
        serviceLogger.error({
          error: new Error('Failed to invoke the OCV NSAT survey trigger'),
          properties: [
            {
              surveyName: configuration.surveyName,
              triggerName: configuration.triggerName,
            },
            error,
          ],
        });
      }
    }, OCV_NSAT_TRIGGER_DELAY_MS);

    return () => window.clearTimeout(timeoutId);
  }, [configuration, enabled]);
};
